import { computed, ref } from 'vue';
import { User } from '@/api/user.js';
import { HELP_SETTINGS_KEY, HELP_PAGE_KEYS, parseHelpSettings } from '@/constants/helpPages.js';
import JwtService from '@/common/jwt.service.js';

const DEFAULT_SETTINGS = {
  isHideHelp: true,
  modules: {},
};

const helpSettings = ref({ ...DEFAULT_SETTINGS });
const helpSettingsVersion = ref(null);
let loadPromise = null;

function normalizeSettings(raw) {
  const parsed = raw && typeof raw === 'object' ? raw : {};

  if (parsed.modules && typeof parsed.modules === 'object') {
    return {
      isHideHelp: Boolean(parsed.isHideHelp),
      modules: { ...parsed.modules },
    };
  }

  const modules = {};
  let isHideHelp = Boolean(parsed.isHideHelp);

  Object.entries(parsed).forEach(([key, value]) => {
    if (key === 'isHideHelp') return;

    if (value === 'hide') {
      isHideHelp = true;
      return;
    }

    if (value === 'open' || value === 'close') {
      modules[key] = value;
    }
  });

  return { isHideHelp, modules };
}

function buildOpenModulesState(currentModules = {}) {
  const modules = { ...currentModules };

  Object.values(HELP_PAGE_KEYS).forEach((moduleKey) => {
    modules[moduleKey] = 'open';
  });

  return modules;
}

async function persistSettings(nextSettings) {
  const saved = await User.saveMeSettings({
    key: HELP_SETTINGS_KEY,
    value: JSON.stringify(nextSettings),
    ...(helpSettingsVersion.value != null ? { version: helpSettingsVersion.value } : {}),
  });

  if (saved?.version != null) {
    helpSettingsVersion.value = saved.version;
  }
}

export async function loadHelpSettings() {
  if (!JwtService.getToken('auth')) return;

  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const data = await User.getMeSettings();
      const list = Array.isArray(data) ? data : [];
      const item = list.find((setting) => setting.key === HELP_SETTINGS_KEY);

      helpSettings.value = normalizeSettings(parseHelpSettings(item?.value));
      helpSettingsVersion.value = item?.version ?? null;
    } catch {
      helpSettings.value = { ...DEFAULT_SETTINGS };
      helpSettingsVersion.value = null;
    }
  })();

  try {
    await loadPromise;
  } finally {
    loadPromise = null;
  }
}

export function useHelpSettings() {
  const isHideHelp = computed(() => helpSettings.value.isHideHelp);

  const getModuleVisualState = (module) => {
    if (!module) return 'close';
    return helpSettings.value.modules[module] ?? 'open';
  };

  const saveHelpSettings = async (patch) => {
    const nextSettings = normalizeSettings({
      ...helpSettings.value,
      ...patch,
      modules: {
        ...helpSettings.value.modules,
        ...(patch.modules || {}),
      },
    });

    helpSettings.value = nextSettings;
    await persistSettings(nextSettings);
  };

  const setModuleVisualState = async (module, state) => {
    if (!module || (state !== 'open' && state !== 'close')) return;

    await saveHelpSettings({
      modules: {
        [module]: state,
      },
    });
  };

  const hideHelpGlobally = async () => {
    await saveHelpSettings({ isHideHelp: true });
  };

  const showHelpGlobally = async () => {
    await saveHelpSettings({
      isHideHelp: false,
      modules: buildOpenModulesState(helpSettings.value.modules),
    });
  };

  return {
    helpSettings,
    isHideHelp,
    loadHelpSettings,
    saveHelpSettings,
    getModuleVisualState,
    setModuleVisualState,
    hideHelpGlobally,
    showHelpGlobally,
  };
}
